// Canonical signout — called from every "Sign out" surface in the
// workspace (AppHeader, IntranetAppHeader, NoOrgOverlay, MemberProfile).
// Centralizes the steps that have to happen in the right order to
// avoid the redirect-loop class of bugs:
//
//   1. await sdk.logout — server-side teardown of the refresh token +
//      TokenManager.clearTokens (which only wipes the SDK's symbols_*
//      keys). Errors swallowed so a network blip doesn't strand the
//      user in a half-signed-out state. The SDK fires SIGNED_OUT to
//      subscribers AFTER clearTokens regardless of network outcome.
//
//   2. invalidateUserDataCache — drops the previous user's cached
//      profile / orgs / projects so the next signed-in render doesn't
//      flash this user's data.
//
//   3. queryClient?.clear — @symbo.ls/fetch's response cache, optional
//      because some callers (early-boot signout via storage event)
//      don't have a queryClient handle. Skipped silently when absent.
//
//   4. purgeAuthStorage — wipes BOTH `sb-<ref>-auth-token` (legacy
//      federation remnants) and `symbols_*` (SDK) namespaces, plus the
//      sdk_only retry flag. WORKSPACE-SIGNOUT-REDIRECT-LOOP: without
//      this, hasSessionToken still returns true on the next page load
//      because stale tokens are intact → redirectIfUnauthed bounces the
//      signed-out user back into the shell. Visible as a flash loop.
//
//   5. localStorage.setItem('logout', Date.now()) — cross-tab signal.
//      wireGlobalListeners listens for `e.key === 'logout'` storage
//      events and triggers the canonical logout switcher in any OTHER
//      tab still signed in for this user. Required so a sign-out in
//      Tab A doesn't leave Tab B mid-session with a stale Supabase
//      auto-refresh writing fresh sb-* tokens back into localStorage.
//      Cleared from the source tab via removeItem so we don't replay
//      our own signal if we re-sign-in later in the same tab.
//
//   6. window.location.href — hard reload (not history.replaceState)
//      so workspace/index.js's redirectIfUnauthed re-evaluates with a
//      fully empty localStorage. SPA-nav leaves the app mounted with
//      auth subscribers and refresh timers still running, which races
//      the just-wiped tokens.
import { purgeAuthStorage } from './clearAuthStorage.js'
import { invalidateUserDataCache } from './sdk.js'

export const signoutAndRedirect = async ({
  sdk,
  queryClient = null,
  redirectTo = '/login'
} = {}) => {
  try {
    await sdk?.logout?.()
  } catch (_) {
    /* network/token-clear failure — still nav */
  }
  try {
    invalidateUserDataCache()
  } catch (_) {}
  try {
    queryClient?.clear?.()
  } catch (_) {}
  try {
    purgeAuthStorage()
  } catch (_) {}
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('logout', String(Date.now()))
      window.localStorage.removeItem('logout')
    }
  } catch (_) {}
  if (typeof window !== 'undefined') window.location.href = redirectTo
}
